import moment from 'moment';
import __ from "lodash";
import React, {useContext, useState} from 'react';
import {useQueryClient} from "react-query";
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

import {MaskedNumber, CurrencyValue, Alert, LightTooltip, DropzoneFileUpload} from "../../../controls";
import AircraftFuelLogDialogValidator from "../validators/AircraftFuelLogDialogValidator";
import SystemContext from "../../../SystemContext";
import useAircraftFuelLogsForTripLeg from "../../../services/aircraft/useAircraftFuelLogsForTripLeg";
import useUpdateAircraftFuelLog from "../../../services/aircraft/useUpdateAircraftFuelLog";
import { Grid, Icon, IconButton, Tooltip, useTheme } from '@material-ui/core';
import Icons from '../../../icons';
import AircraftFuelUpliftDialog from './AircraftFuelUpliftDialog';
import { fetch as fetchUnitsOfMeasure } from '../../../services/settings/useUnitsOfMeasure';
import MenuIcon from "@material-ui/icons/Menu";
import {PageviewOutlined} from "@material-ui/icons";
import { ReactComponent as ChecklistStatusIndicator } from '../../../assets/ChecklistStatusIndicator.svg';

const AircraftFuelLogDialog = (props) => {
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const eventBus = SystemData.eventBus;
  const queryClient = useQueryClient();

  let uomData = null;
  fetchUnitsOfMeasure({
    queryClient,
    useCache: true,
    extraKeys: [],
  }).then(data => {
    uomData = data;
  });

  const StatusIndicator = (props) => {
    const {color, tooltip, classes} = props;
    if (color === "none") {
      return null;
    }
    return (    
      <div style={{position: "absolute", top: -2, right: 0}}>
        <Tooltip title={tooltip} >
          <div>
            <ChecklistStatusIndicator style={{fill: `${color||'#ff0000'}`, stroke: "#777777", width: 10, height: 10 }}/>
          </div>
        </Tooltip>
      </div>
    )
  }
  
  const USGtoKG = (props) => {
    const value = props.value;
    let uomRecord = __.find(uomData, {
      uomCode: 'KG',
      toUomCode: "USG",
    });
    const convertedValue = value / (uomRecord?.factor||1);
    return (
      <MaskedNumber
        value={convertedValue}
        decimalScale={0}
        fixedDecimalScale={true}
        style={{fontSize: "0.875rem"}}
        type="text"
        displayType="text"
      />
    )
  }

  let tripLegUuid = null;
  if (props.legRecord && props.legRecord.tripLegUuid) {
    tripLegUuid = props.legRecord.tripLegUuid;
  }

  const [fuelLog, setFuelLog] = useState(null);
  const [record, setRecord] = useState({
    open: props.open,
    tripLegUuid: null
  });

  const emptyUpliftLine = {
    upliftFuelQty: 0,
    upliftFuelUom: 'LTR',
    upliftedKg: 0,
    upliftedValue: 0,
    upliftedPricePerKg: 0,
    upliftFuelUsg: 0
  }

  const canEdit = props.canEdit;

  if (props.open !== record.open || tripLegUuid !== record.tripLegUuid) {
    setRecord({
      open: props.open,
      tripLegUuid 
    });
    setFuelLog(null);
  }

  const extraKeys = [tripLegUuid];
  const aircraftFuelLog = useAircraftFuelLogsForTripLeg({queryClient, extraKeys});
  const handleUpdateAircraftFuelLog = useUpdateAircraftFuelLog();

  const [validationState, setValidationState] = useState({
    error: true,
    errorMessage: "checking",
    field: null
  });

  const handleClose = () => {
    if (props.onChange)
      props.onChange({state: 'cancel'});
  };

  const handleSave = () => {
    save();
    // if (props.onChange)
    //   props.onChange({state: 'save', record});
  };

  async function save (){
    if (validationState.error) {
      SystemData.openAlert("Error", validationState.errorMessage);
      if (validationState.field) {
        const field = document.getElementById(validationState.field);
        if (field) {
          field.focus();
        }
      }
    } else {
      handleUpdateAircraftFuelLog.mutate({
        record: fuelLog,
        oldRecord: aircraftFuelLog.data,
        extraKeys,
        postSuccess: (params => {
          props.onChange({state: 'save', record: {...fuelLog}, oldRecord: aircraftFuelLog.data, extraKeys});
          queryClient.invalidateQueries("AircraftFuelLogs");
          queryClient.invalidateQueries("AircraftFuelLogs", "TripLeg");
          tripLegUuid = null;
          setRecord({...record, tripLegUuid});
          setFuelLog(null);
        }),
        postError: (params => {
          SystemData.openAlert('Unexpected Error', 'An unexpected error occurred saving the fuel log');
        })
      });
    }
  }

  if (aircraftFuelLog.data && !fuelLog) {
    setFuelLog(aircraftFuelLog.data);
  }

  const getUpliftedTotals = () => {
    let upliftedKg = 0;
    let upliftFuelUsg = 0;
    let upliftedValue = 0;
    let upliftedPricePerKg = 0;

    const upliftedFuelDetails = fuelLog.upliftedFuelDetails;
    upliftedFuelDetails.forEach(upliftLine => {
      upliftedKg += upliftLine.upliftedKg;
      upliftFuelUsg += upliftLine.upliftFuelUsg;
      upliftedValue += upliftLine.upliftedValue;
    });

    upliftedPricePerKg = upliftedValue / upliftedKg;

    return {upliftedKg, upliftFuelUsg, upliftedValue, upliftedPricePerKg}
  }

  const valueChanged = (value, element, line) => {
    if (['openingFuelKg', 'openingFuelValue'].indexOf(element) >= 0) {
      const changedFuelLog = {...fuelLog};
      changedFuelLog[element] = value;

      if (changedFuelLog.openingFuelKg !== changedFuelLog.originalOpeningFuelKg || changedFuelLog.openingFuelValue !== changedFuelLog.originalOpeningFuelValue) {
        changedFuelLog.overrideOpening = true;
      }
      // changedFuelLog.upliftedKg = Math.max(0, changedFuelLog.blockFuelKg - changedFuelLog.openingFuelKg);
      changedFuelLog.upliftedPricePerKg = changedFuelLog.upliftedValue / changedFuelLog.upliftedKg;
      // changedFuelLog.blockFuelValue = changedFuelLog.openingFuelValue + changedFuelLog.upliftedValue;
      changedFuelLog.blockPricePerKg = changedFuelLog.blockFuelValue / changedFuelLog.blockFuelKg;
      changedFuelLog.remainingFuelValue = changedFuelLog.remainingFuelKg * changedFuelLog.blockPricePerKg;
      changedFuelLog.usedFuelValue = changedFuelLog.usedFuelKg * changedFuelLog.blockPricePerKg;
      setFuelLog(changedFuelLog);
    }
    if (['upliftedKg'].indexOf(element) >= 0) {
      const upliftedFuelDetails = fuelLog.upliftedFuelDetails;
      const upliftLine = upliftedFuelDetails[line];

      upliftLine['upliftedKg'] = value;
      upliftLine['upliftFuelUsg'] = 0;
      upliftLine['upliftedValue'] = fuelLog.pricePerUsg * upliftLine.upliftFuelUsg;;
      upliftLine['upliftedPricePerKg'] = upliftLine.upliftedValue / upliftLine.upliftedKg;

      const changes = getUpliftedTotals();
      const changedFuelLog = {...fuelLog, upliftedFuelDetails, ...changes};

      // changedFuelLog.blockFuelKg = changedFuelLog.upliftedKg + changedFuelLog.openingFuelKg;
      changedFuelLog.blockFuelValue = changedFuelLog.openingFuelValue + changedFuelLog.upliftedValue;
      changedFuelLog.blockPricePerKg = changedFuelLog.blockFuelValue / changedFuelLog.blockFuelKg;
      changedFuelLog.remainingFuelValue = changedFuelLog.remainingFuelKg * changedFuelLog.blockPricePerKg;
      changedFuelLog.usedFuelValue = changedFuelLog.usedFuelKg * changedFuelLog.blockPricePerKg;
      setFuelLog(changedFuelLog);
    }
    if (['blockFuelKg'].indexOf(element) >= 0) {
      const changedFuelLog = {...fuelLog};
      changedFuelLog[element] = value;

      changedFuelLog.blockFuelValue = changedFuelLog.openingFuelValue + changedFuelLog.upliftedValue;
      changedFuelLog.blockPricePerKg = changedFuelLog.blockFuelValue / changedFuelLog.blockFuelKg;
      changedFuelLog.remainingFuelKg = changedFuelLog.blockFuelKg - changedFuelLog.usedFuelKg;
      changedFuelLog.remainingFuelValue = changedFuelLog.remainingFuelKg * changedFuelLog.blockPricePerKg;
      changedFuelLog.usedFuelValue = changedFuelLog.usedFuelKg * changedFuelLog.blockPricePerKg;
      setFuelLog(changedFuelLog);
    }
    if (['usedFuelKg'].indexOf(element) >= 0) {
      const changedFuelLog = {...fuelLog};
      changedFuelLog[element] = value;
      changedFuelLog.remainingFuelKg = changedFuelLog.blockFuelKg - changedFuelLog.usedFuelKg;
      changedFuelLog.remainingFuelValue = changedFuelLog.remainingFuelKg * changedFuelLog.blockPricePerKg;
      changedFuelLog.usedFuelValue = changedFuelLog.usedFuelKg * changedFuelLog.blockPricePerKg;
      setFuelLog(changedFuelLog);
    }
    if (['remainingFuelKg'].indexOf(element) >= 0) {
      const changedFuelLog = {...fuelLog};
      changedFuelLog[element] = value;
      changedFuelLog.usedFuelKg = changedFuelLog.blockFuelKg - changedFuelLog.remainingFuelKg;
      changedFuelLog.remainingFuelValue = changedFuelLog.remainingFuelKg * changedFuelLog.blockPricePerKg;
      changedFuelLog.usedFuelValue = changedFuelLog.usedFuelKg * changedFuelLog.blockPricePerKg;
      setFuelLog(changedFuelLog);
    }
  }

  const handleReset = () => {
    setFuelLog({
      ...fuelLog,
      overrideOpening: false,
      openingFuelKg: fuelLog.originalOpeningFuelKg,
      openingFuelValue: fuelLog.openingFuelValue
    });
  }

  const handleUpliftLine = () => {
    const upliftedFuelDetails = fuelLog.upliftedFuelDetails;
    upliftedFuelDetails.push(emptyUpliftLine);
    setFuelLog({
      ...fuelLog,
      upliftedFuelDetails
    })
  }
  const handleDeleteUpliftLine = (line) => {
    const upliftedFuelDetails = fuelLog.upliftedFuelDetails;
    upliftedFuelDetails.splice(line, 1);

    const changes = getUpliftedTotals();
    const changedFuelLog = {...fuelLog, upliftedFuelDetails, ...changes};

    // changedFuelLog.blockFuelKg = changedFuelLog.upliftedKg + changedFuelLog.openingFuelKg;
    changedFuelLog.blockFuelValue = changedFuelLog.openingFuelValue + changedFuelLog.upliftedValue;
    changedFuelLog.blockPricePerKg = changedFuelLog.blockFuelValue / changedFuelLog.blockFuelKg;
    changedFuelLog.remainingFuelValue = changedFuelLog.remainingFuelKg * changedFuelLog.blockPricePerKg;
    changedFuelLog.usedFuelValue = changedFuelLog.usedFuelKg * changedFuelLog.blockPricePerKg;
    setFuelLog(changedFuelLog);
  }

  const handledClickUpliftedFuel = (line) => {
    const upliftedFuelDetails = fuelLog.upliftedFuelDetails;
    const upliftLine = upliftedFuelDetails[line];

    const record = {
      upliftFuelQty: upliftLine.upliftFuelQty||0,
      upliftFuelUom: upliftLine.upliftFuelUom||'',
      upliftFuelUsg: upliftLine.upliftFuelUsg||0,
      usgConversionFactor: 1,
      line
    }
    const title = `Uplifted Aircraft Fuel`; // for ${props.legRecord.airlineCode}${props.legRecord.flightNo} ${moment(props.legRecord.flightDate, 'YYYY-MM-DD').format('DD/MMM')}`;
    eventBus.trigger('AircraftFuelUpliftDialog.open', {canEdit, title, record});
  }

  const handleUpliftValueChanges = (record, updateLoggedPrice) => {
    const {upliftFuelQty, upliftFuelUom, upliftFuelUsg, line} = record;

    const upliftedFuelDetails = fuelLog.upliftedFuelDetails;
    const upliftLine = upliftedFuelDetails[line];

    upliftLine['upliftFuelQty'] = upliftFuelQty;
    upliftLine['upliftFuelUom'] = upliftFuelUom;
    upliftLine['upliftFuelUsg'] = upliftFuelUsg;
    upliftLine['upliftedValue'] = fuelLog.pricePerUsg * upliftLine.upliftFuelUsg;
    upliftLine['upliftedPricePerKg'] = upliftLine.upliftedValue / upliftLine.upliftedKg;
    if (updateLoggedPrice) {
      fuelLog['loggedPricePerUsg'] = fuelLog.pricePerUsg;
    }

    const changes = getUpliftedTotals();
    const changedFuelLog = {...fuelLog, upliftedFuelDetails, ...changes};

    // changedFuelLog.blockFuelKg = changedFuelLog.upliftedKg + changedFuelLog.openingFuelKg;
    changedFuelLog.blockFuelValue = changedFuelLog.openingFuelValue + changedFuelLog.upliftedValue;
    changedFuelLog.blockPricePerKg = changedFuelLog.blockFuelValue / changedFuelLog.blockFuelKg;
    changedFuelLog.remainingFuelValue = changedFuelLog.remainingFuelKg * changedFuelLog.blockPricePerKg;
    changedFuelLog.usedFuelValue = changedFuelLog.usedFuelKg * changedFuelLog.blockPricePerKg;
    setFuelLog(changedFuelLog);
  }

  const handledRecalculateUpliftedFuelClick = (line) => {
    const upliftedFuelDetails = fuelLog.upliftedFuelDetails;
    const upliftLine = upliftedFuelDetails[line];

    const record = {
      upliftFuelQty: upliftLine.upliftFuelQty||0,
      upliftFuelUom: upliftLine.upliftFuelUom||'',
      upliftFuelUsg: upliftLine.upliftFuelUsg||0,
      usgConversionFactor: 1,
      line
    };

    handleUpliftValueChanges(record, true);
  }

  const renderFileName = (params) => {
    const urlArray = params && params.value ? params.value.split("/") : [];
    const fileNameArray = urlArray && urlArray.length > 0 ? urlArray[urlArray.length - 1].split("_") : [];
    return (
      <DropzoneFileUpload
        objectType="FLIGHT"
        tripLegUuid={params.tripLegUuid}
        documentUuid={" "}
        accept=".doc, .docx, .xls, .xlsx, .pdf"
        dropzoneStyle={{ height: 38,  maxHeight: 38, minHeight: 38, border: "1px dashed #929292" }}
        inputLabel={{fontSize: 17, color: "#fd5b02"}}
        inputContent={fileNameArray && fileNameArray.length > 0 ? fileNameArray[fileNameArray.length - 1] : "Drop a File or Click to browse"}
        disabled={false}
        onChange={(result)=>{
          if (result && result.status) {
            const fileType = result.fileName.split('.');
            const fileSize = result.fileSize > 1000000
              ? `${(result.fileSize / 1000000).toFixed(0)}MB`
              : result.fileSize > 1000
                ? `${(result.fileSize / 1000).toFixed(0)}KB`
                : `${result.fileSize}B`
            const changeLog = {
              ...fuelLog,
              fileName: result.fileName,
              fileType: Array.isArray(fileType) && fileType.length > 1 ? fileType[fileType.length - 1] : "",
              fileSize: fileSize,
              journeyLogFile: result.url,
            };
            setFuelLog(changeLog);
          }
        }}
      />
    )
  }


  if (props.open && fuelLog && aircraftFuelLog.status === "success"){
    return (
      <div id={"AircraftFuelLogDialog"}>
        <Dialog open={props.open}
                onClose={handleClose}
                aria-labelledby="form-dialog-title"
                disableBackdropClick={true}
                minWidth="1130px"
                maxWidth="1130px"
        >
          <DialogTitle id="form-dialog-title">
            <div style={{clear: "both", backgroundColor: theme.palette.primary.main, padding:"10px"}}>
              Fuel Log
            </div>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              <div width={1030} style={{width: 1030, minWidth: 1030, whiteSpace: "pre" }}>
                <Grid container spacing={1}>
                  <Grid item xs={4} >
                    Flight: <strong>{props.legRecord.airlineCode}{props.legRecord.flightNo} {moment(props.legRecord.flightDate, 'YYYY-MM-DD').format('DD/MMM')}</strong>      {props.legRecord.originAirportCode} - {props.legRecord.destinationAirportCode} ({props.legRecord.aircraftReg})
                  </Grid>
                  <Grid item xs={2} >
                    <div style={{textAlign: "right"}}>{"Journey Log:"}</div>
                  </Grid>
                  <Grid item xs={6} >
                    <div style={{display: "flex", }}>
                      {renderFileName({value: fuelLog.journeyLogFile, tripLegUuid: props.legRecord.tripLegUuid})}
                      <IconButton
                        color="inherit"
                        disabled={!fuelLog.journeyLogFile}
                        aria-label="open menu"
                        aria-haspopup="true"
                        onClick={()=> {window.open(fuelLog.journeyLogFile, "_blank");}}
                      >
                        <PageviewOutlined style={{width: 32, height: 32, color: fuelLog.journeyLogFile ? 'primary' : '#BDBDBD', marginTop: "-8" }} />
                      </IconButton>
                    </div>
                  </Grid>
                </Grid>
              </div>
            </DialogContentText>

            <div style={{width: "100%", height: 280+(42 * fuelLog.upliftedFuelDetails.length)}}>
              <div style={{display: "flex", flexDirection: "column"}}>
                <div style={{marginBottom: "5px", display: "flex", justifyContent: "space-between"}}></div>
                <div style={{flex: "1 1 0px"}}>
                  <div id="myGrid" className="ag-theme-alpine" style={{height: 280+(42 * fuelLog.upliftedFuelDetails.length), width: "100%"}}>
                    <div style={{height: "100%"}}>
                      <div className="ag-root-wrapper ag-layout-normal ag-ltr">
                        <div className="ag-root-wrapper-body ag-layout-normal">
                          <div className="ag-root ag-unselectable ag-layout-normal" role="grid">

                            <div className="ag-header ag-pivot-off" role="presentation" style={{height: "49px", minHeight: "49px"}}>
                              <div className="ag-header-row ag-header-row-column" role="row" style={{top: "0px", height: "48px", width: "1030px"}}>
                                <div className="ag-header-cell" style={{width: "166px", left: "0px"}}>
                                  <div className="ag-cell-label-container" role="presentation">
                                    <span className="ag-header-cell-text"> </span>
                                  </div>
                                </div>
                                <div className="ag-header-cell ag-right-aligned-header" style={{width: "166px", left: "166px"}}>
                                  <div className="ag-cell-label-container" role="presentation">
                                    <span className="ag-header-cell-text ag-right-aligned-cell" style={{width: "100%"}}>Fuel in KG</span>
                                  </div>
                                </div>
                                <div className="ag-header-cell ag-right-aligned-header" style={{width: "166px", left: "332px"}}>
                                  <div className="ag-cell-label-container" role="presentation">
                                    <span className="ag-header-cell-text ag-right-aligned-cell" style={{width: "100%"}}>Fuel Value</span>
                                  </div>
                                </div>
                                <div className="ag-header-cell ag-right-aligned-header" style={{width: "166px", left: "498px"}}>
                                  <div className="ag-cell-label-container" role="presentation">
                                    <span className="ag-header-cell-text ag-right-aligned-cell" style={{width: "100%"}}>Price per KG</span>
                                  </div>
                                </div>
                                <div className="ag-header-cell ag-right-aligned-header" style={{width: "166px", left: "664px"}}>
                                  <div className="ag-cell-label-container" role="presentation">
                                    <span className="ag-header-cell-text ag-right-aligned-cell"  style={{width: "100%"}}>Fuel in USG</span>
                                  </div>
                                </div>
                                <div className="ag-header-cell ag-right-aligned-header" style={{width: "166px", left: "830px"}}>
                                  <div className="ag-cell-label-container" role="presentation">
                                    <span className="ag-header-cell-text ag-right-aligned-cell"  style={{width: "100%"}}>Price per USG</span>
                                  </div>
                                </div>
                                <div className="ag-header-cell ag-right-aligned-header" style={{width: "30px", left: "996px"}}>
                                  <div className="ag-cell-label-container" role="presentation">
                                    <span className="ag-header-cell-text ag-right-aligned-cell"  style={{width: "100%"}}>&nbsp;</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {canEdit
                              ? <AircraftFuelLogDialogValidator
                                  record={fuelLog}
                                  onChange={(validation) => {
                                    setTimeout(()=> {
                                      setValidationState(validation);
                                    },1);
                                  }}
                                />
                              :<></>
                            }
                            {canEdit
                              ? <Alert
                                  open={validationState.error}
                                  severity="error"
                                  canClose={false}
                                  message={validationState.errorMessage ? validationState.errorMessage : ""}
                                />
                              :<></>
                            }
                            <div className="ag-body-viewport ag-layout-normal ag-row-no-animation" >
                              <div className="ag-center-cols-clipper"style={{height: "210px"}}>
                                <div className="ag-center-cols-viewport" style={{height: "calc(100% + 0px)"}}>
                                  <div className="ag-center-cols-container" style={{width:"1030px", height: "210px"}}>

                                  {/* ==== Opening Line ==== */}

                                    <div className="ag-row" style={{height: "42px"}}>
                                      <div className="ag-cell  ag-cell-auto-height  ag-cell-focus" style={{width: "166px", left: "0px"}}>Opening</div>
                                      <div className="ag-cell ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "166px", paddingLeft: "17px", paddingRight: "17px", boxShadow: "none", border: "0px", height: "calc(100% - 1px)"}}>
                                        <MaskedNumber
                                          displayType={canEdit?'input':"text"}
                                          decimalScale={0}
                                          fixedDecimalScale={true}
                                          style= {{width:"100%", fontSize: "0.875rem", textAlign: "right"}}
                                          value={fuelLog.openingFuelKg}
                                          suffix=" kg"
                                          allowNegative={false}
                                          onChange={(value)=> {
                                            valueChanged(value, 'openingFuelKg');
                                          }}
                                        />
                                      </div>
                                      <div className="ag-cell ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "332px", paddingLeft: "17px", paddingRight: "17px", boxShadow: "none", border: "0px", height: "calc(100% - 1px)"}}>
                                        <MaskedNumber
                                          displayType={canEdit?'input':"text"}
                                          decimalScale={2}
                                          fixedDecimalScale={true}
                                          style= {{width:"100%", fontSize: "0.875rem", textAlign: "right"}}
                                          value={fuelLog.openingFuelValue}
                                          prefix="$ "
                                          allowNegative={false}
                                          onChange={(value)=> {
                                            valueChanged(value, 'openingFuelValue');
                                          }}
                                        />
                                      </div>
                                      <div className="ag-cell ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "498px"}}></div>
                                      <div className="ag-cell  ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "664px"}}></div>
                                      <div className="ag-cell  ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "830px"}}></div>
                                    </div>

                                  {/* ==== Uplift Lines ==== */}

                                    {fuelLog.upliftedFuelDetails.map((upliftLine, i) => (
                                      <div className="ag-row" style={{height: "42px"}}>
                                        <div className="ag-cell  ag-cell-auto-height  ag-cell-focus" style={{width: "166px", left: "0px"}}>Uplifted</div>
                                        <div className="ag-cell ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "166px", paddingLeft: "17px", paddingRight: "17px", boxShadow: "none", border: "0px", height: "calc(100% - 1px)"}}>
                                          <MaskedNumber
                                            displayType={canEdit?'input':"text"}
                                            decimalScale={0}
                                            fixedDecimalScale={true}
                                            style= {{width:"100%", fontSize: "0.875rem", textAlign: "right"}}
                                            value={upliftLine.upliftedKg}
                                            suffix=" kg"
                                            allowNegative={false}
                                            onChange={(value)=> {
                                              valueChanged(value, 'upliftedKg', i);
                                            }}
                                          />
                                        </div>
                                        <div className="ag-cell ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "332px", paddingLeft: "17px", paddingRight: "17px", boxShadow: "none", border: "0px", height: "calc(100% - 1px)"}}>
                                        <CurrencyValue
                                            displayType="text"
                                            decimalScale={2}
                                            fixedDecimalScale={true}
                                            style= {{width:"100%", fontSize: "0.875rem", textAlign: "right"}}
                                            value={upliftLine.upliftedValue}
                                          />
                                        </div>
                                        <div className="ag-cell ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "498px"}}>
                                          <CurrencyValue
                                            displayType="text"
                                            decimalScale={5}
                                            fixedDecimalScale={true}
                                            style= {{width:"100%", fontSize: "0.875rem", textAlign: "right"}}
                                            value={upliftLine.upliftedPricePerKg}
                                            suffix=" / kg"
                                          />
                                        </div>
                                        <div className="ag-cell  ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "664px"}}>
                                          <div style={{display: "inline-block"}}><IconButton disabled={upliftLine.upliftedKg === 0 || upliftLine.upliftedKg === null} onClick={()=>{handledClickUpliftedFuel(i)}}><Icons>CalculatorIcon</Icons></IconButton></div>
                                          <div style={{display: "inline-block"}}>
                                            <LightTooltip title={
                                              <Grid container key={i} style={{width: 90}}>
                                                <Grid item xs={8} nowrap="true" style={{ minWidth: 10, textAlign: "right" }}>
                                                  <MaskedNumber
                                                    displayType="text"
                                                    value={upliftLine.upliftFuelQty}
                                                    decimalScale={0}
                                                    fixedDecimalScale={true}
                                                    style={{fontSize: "0.875rem"}}
                                                  />
                                                </Grid>
                                                <Grid item xs={4} nowrap="true" style={{ minWidth: 10, paddingLeft: 3, textAlign: "left" }}>
                                                  {upliftLine.upliftFuelUom}
                                                </Grid>
                                                <Grid item xs={8} nowrap="true" style={{ minWidth: 10, textAlign: "right" }}>
                                                  <MaskedNumber
                                                    value={upliftLine.upliftFuelUsg}
                                                    decimalScale={0}
                                                    fixedDecimalScale={true}
                                                    style={{fontSize: "0.875rem"}}
                                                    displayType="text"
                                                  />
                                                </Grid>
                                                <Grid item xs={4} nowrap="true" style={{ minWidth: 10, paddingLeft: 3, textAlign: "left" }}>
                                                  USG
                                                </Grid>
                                                <Grid item xs={8} nowrap="true" style={{ minWidth: 10, textAlign: "right" }}>
                                                  <USGtoKG
                                                    value={upliftLine.upliftFuelUsg}
                                                  />
                                                </Grid>
                                                <Grid item xs={4} nowrap="true" style={{ minWidth: 10, paddingLeft: 3, textAlign: "left" }}>
                                                  KG
                                                </Grid>
                                              </Grid>
                                            }>
                                              <div>
                                                <MaskedNumber
                                                  displayType="text"
                                                  decimalScale={0}
                                                  fixedDecimalScale={true}
                                                  style= {{display: "inline-block", width:"100%", fontSize: "0.875rem", textAlign: "right"}}
                                                  value={upliftLine.upliftFuelUsg}
                                                  suffix=" usg"
                                                />
                                              </div>
                                            </LightTooltip>
                                          </div>
                                        </div>
                                        <div className="ag-cell  ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "830px"}}>
                                        {fuelLog.loggedPricePerUsg && fuelLog.pricePerUsg !== fuelLog.loggedPricePerUsg ?
                                          <>
                                          <StatusIndicator tooltip={"Logged value is different from fuel price of " + fuelLog.pricePerUsg}/>
                                          <CurrencyValue
                                            displayType="text"
                                            decimalScale={5}
                                            fixedDecimalScale={true}
                                            style= {{width:"100%", fontSize: "0.875rem", textAlign: "right", color: "#ff0000"}}
                                            value={fuelLog.loggedPricePerUsg}
                                            suffix=" / usg"
                                          />
                                          <div style={{position: "absolute", top: -2, right: -26}}>
                                            <Tooltip title={"Recalculate using current fuel price"}>
                                              <IconButton style={{padding: 0}} onClick={()=>{handledRecalculateUpliftedFuelClick(i)}}>
                                                <Icon>refresh</Icon>
                                              </IconButton>
                                            </Tooltip>
                                          </div>
                                          </>
                                        :
                                          <CurrencyValue
                                            displayType="text"
                                            decimalScale={5}
                                            fixedDecimalScale={true}
                                            style= {{width:"100%", fontSize: "0.875rem", textAlign: "right"}}
                                            value={fuelLog.pricePerUsg}
                                            suffix=" / usg"
                                          />
                                        }
                                        </div>
                                        <div className="ag-cell  ag-cell-auto-height ag-right-aligned-cell" style={{width: "30px", left: "996px", padding: 0}}>
                                          {i === 0?null:
                                            <IconButton style={{padding: 0}} onClick={()=>{handleDeleteUpliftLine(i)}}><Icon>delete_outline</Icon></IconButton>
                                          }
                                        </div>
                                      </div>
                                    ))}
                                  {/* ==== Block Line ==== */}

                                    <div className="ag-row" style={{height: "42px"}}>
                                      <div className="ag-cell  ag-cell-auto-height  ag-cell-focus" style={{width: "166px", left: "0px"}}>Block</div>
                                      <div className="ag-cell ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "166px", paddingLeft: "17px", paddingRight: "17px", boxShadow: "none", border: "0px", height: "calc(100% - 1px)"}}>
                                        <MaskedNumber
                                          displayType={canEdit?'input':"text"}
                                          decimalScale={0}
                                          fixedDecimalScale={true}
                                          style= {{width:"100%", fontSize: "0.875rem", textAlign: "right"}}
                                          value={fuelLog.blockFuelKg}
                                          suffix=" kg"
                                          allowNegative={false}
                                          onChange={(value)=> {
                                            valueChanged(value, 'blockFuelKg');
                                          }}
                                        />
                                      </div>
                                      <div className="ag-cell ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "332px", paddingLeft: "17px", paddingRight: "17px", boxShadow: "none", border: "0px", height: "calc(100% - 1px)"}}>
                                      <CurrencyValue
                                          displayType="text"
                                          decimalScale={2}
                                          fixedDecimalScale={true}
                                          style= {{width:"100%", fontSize: "0.875rem", textAlign: "right"}}
                                          value={fuelLog.blockFuelValue}
                                        />
                                      </div>
                                      <div className="ag-cell ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "498px"}}>
                                        <CurrencyValue
                                          displayType="text"
                                          decimalScale={5}
                                          fixedDecimalScale={true}
                                          style= {{width:"100%", fontSize: "0.875rem", textAlign: "right"}}
                                          value={fuelLog.blockPricePerKg}
                                          suffix=" / kg"
                                        />
                                      </div>
                                      <div className="ag-cell  ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "664px"}}>
                                      </div>
                                      <div className="ag-cell  ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "830px"}}>
                                      </div>
                                    </div>

                                  {/* ==== Used Line ==== */}

                                    <div className="ag-row" style={{height: "42px"}}>
                                      <div className="ag-cell  ag-cell-auto-height  ag-cell-focus" style={{width: "166px", left: "0px"}}>Used</div>
                                      <div className="ag-cell ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "166px", paddingLeft: "17px", paddingRight: "17px", boxShadow: "none", border: "0px", height: "calc(100% - 1px)"}}>
                                        <MaskedNumber
                                          displayType={canEdit?'input':"text"}
                                          decimalScale={0}
                                          fixedDecimalScale={true}
                                          style= {{width:"100%", fontSize: "0.875rem", textAlign: "right"}}
                                          value={fuelLog.usedFuelKg}
                                          suffix=" kg"
                                          allowNegative={false}
                                          onChange={(value)=> {
                                            valueChanged(value, 'usedFuelKg');
                                          }}
                                        />
                                      </div>
                                      <div className="ag-cell ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "332px", paddingLeft: "17px", paddingRight: "17px", boxShadow: "none", border: "0px", height: "calc(100% - 1px)"}}>
                                      <CurrencyValue
                                          displayType="text"
                                          decimalScale={2}
                                          fixedDecimalScale={true}
                                          style= {{width:"100%", fontSize: "0.875rem", textAlign: "right"}}
                                          value={fuelLog.usedFuelValue}
                                        />
                                      </div>
                                      <div className="ag-cell ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "498px"}}>
                                      </div>
                                      <div className="ag-cell  ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "664px"}}>
                                      </div>
                                      <div className="ag-cell  ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "830px"}}>
                                      </div>
                                    </div>

                                  {/* ==== Remaining Line ==== */}

                                    <div className="ag-row" style={{height: "42px"}}>
                                      <div className="ag-cell  ag-cell-auto-height  ag-cell-focus" style={{width: "166px", left: "0px"}}>Remaining</div>
                                      <div className="ag-cell ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "166px", paddingLeft: "17px", paddingRight: "17px", boxShadow: "none", border: "0px", height: "calc(100% - 1px)"}}>
                                        <MaskedNumber
                                          displayType={canEdit?'input':"text"}
                                          decimalScale={0}
                                          fixedDecimalScale={true}
                                          style= {{width:"100%", fontSize: "0.875rem", textAlign: "right"}}
                                          value={fuelLog.remainingFuelKg}
                                          suffix=" kg"
                                          onChange={(value)=> {
                                            valueChanged(value, 'remainingFuelKg');
                                          }}
                                        />
                                      </div>
                                      <div className="ag-cell ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "332px", paddingLeft: "17px", paddingRight: "17px", boxShadow: "none", border: "0px", height: "calc(100% - 1px)"}}>
                                      <CurrencyValue
                                          displayType="text"
                                          decimalScale={2}
                                          fixedDecimalScale={true}
                                          style= {{width:"100%", fontSize: "0.875rem", textAlign: "right"}}
                                          value={fuelLog.remainingFuelValue}
                                        />
                                      </div>
                                      <div className="ag-cell ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "498px"}}>
                                      </div>
                                      <div className="ag-cell  ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "664px"}}>
                                      </div>
                                      <div className="ag-cell  ag-cell-auto-height ag-right-aligned-cell" style={{width: "166px", left: "830px"}}>
                                      </div>
                                    </div>


                                  </div>
                                </div>
                              </div>
                            </div>


                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </DialogContent>
          {canEdit?
            <DialogActions>
              <Button onClick={handleUpliftLine} color="secondary" disabled={fuelLog.upliftedFuelDetails.length >=5}>
                Add Uplift Line
              </Button>
              <Button onClick={handleReset} color="secondary" disabled={!fuelLog.overrideOpening}>
                Reset Opening Fuel
              </Button>
              <Button onClick={handleClose} color="primary">
                Cancel
              </Button>
              <Button onClick={handleSave} color="primary">
                Save
              </Button>
            </DialogActions>
            :
            <DialogActions>
              <Button onClick={handleClose} color="primary">
                Ok
              </Button>
            </DialogActions>
          }
        </Dialog>
        <AircraftFuelUpliftDialog
          systemData={SystemData}
          onChange={handleUpliftValueChanges}
        />
      </div>
    );
  } else {
    return (<></>);
  }
}

export default AircraftFuelLogDialog;
